import { motion } from "framer-motion";
import { Phone, Mail, MapPin, MessageCircle } from "lucide-react";

/**
 * FounderSection — "Tvár za projektom".
 *
 * Emmy audit finding: Anonymous websites lose trust. Show the person behind
 * the company. Uses a stylized placeholder (initials in a monogram) until
 * the client provides a real portrait — image URL is easy to swap here.
 */
const FOUNDER = {
  name: "Ján Mika",
  role: "Zakladateľ · RESTOB s.r.o.",
  initials: "JM",
  yearsExperience: 15,
  completedHomes: 172,
  phone: "0907 64 99 64",
  email: "supermajstri.sk@gmail.com",
  location: "Bratislava · Podunajsko",
  photo: "/founder-avatar.jpg",
  quote:
    "Osobne dohliadam na každú stavbu, ktorú u nás objednáte. Cena, ktorú vám podpíšem, sa nemení. Fotky posielame denne, aby ste vedeli presne, čo sa deje. Tak ako by som staval pre svoju rodinu.",
};

export default function FounderSection() {
  return (
    <section
      id="o-nas"
      className="py-20 bg-gradient-to-b from-[#0a0a0a] to-[#0f0f0f] border-y border-white/5"
      data-testid="founder-section"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-12">
          <div className="text-[10px] uppercase tracking-widest text-[#FF5E00] font-black mb-3">
            Tvár za projektom · Reálna firma · Reálni ľudia
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95]">
            Nie sme <span className="text-zinc-500">agentúra</span>.
            <br />
            Sme <span className="text-[#00E5FF] neon-cyan">stavbári</span>
            <span className="text-[#FF5E00]">.</span>
          </h2>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="relative overflow-hidden rounded-3xl border border-white/10 bg-black/40 backdrop-blur-sm"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-[#00E5FF]/5 via-transparent to-[#FF5E00]/5 pointer-events-none" />

          <div className="relative grid md:grid-cols-[280px_1fr] gap-8 p-6 sm:p-10">
            {/* Portrait / monogram placeholder */}
            <div className="flex flex-col items-center md:items-start">
              <div className="relative w-40 h-40 sm:w-56 sm:h-56 rounded-2xl overflow-hidden bg-gradient-to-br from-[#00E5FF]/20 via-[#0a0a0a] to-[#FF5E00]/20 border border-white/10 flex items-center justify-center">
                {FOUNDER.photo ? (
                  <img
                    src={FOUNDER.photo}
                    alt={`${FOUNDER.name} — ${FOUNDER.role}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="text-center" aria-hidden="true">
                    <div className="text-6xl sm:text-7xl font-black bg-gradient-to-br from-[#00E5FF] to-[#FF5E00] bg-clip-text text-transparent">
                      {FOUNDER.initials}
                    </div>
                    <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mt-2">
                      Foto čoskoro
                    </div>
                  </div>
                )}
                <div className="absolute bottom-2 right-2 w-3 h-3 rounded-full bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.6)]" title="Práve dostupný"></div>
              </div>

              {/* Stats under portrait */}
              <div className="mt-6 flex md:flex-col gap-4 md:gap-3 text-center md:text-left">
                <div>
                  <div className="text-2xl font-black text-[#00E5FF]">{FOUNDER.yearsExperience}+</div>
                  <div className="text-xs text-zinc-500 mono uppercase tracking-widest">rokov v stavebníctve</div>
                </div>
                <div>
                  <div className="text-2xl font-black text-[#00E5FF]">{FOUNDER.completedHomes}</div>
                  <div className="text-xs text-zinc-500 mono uppercase tracking-widest">dokončených stavieb</div>
                </div>
              </div>
            </div>

            {/* Bio + quote */}
            <div>
              <div className="mb-4">
                <h3 className="text-3xl sm:text-4xl font-black tracking-tight text-white">
                  {FOUNDER.name}
                </h3>
                <p className="text-sm text-[#00E5FF] mono uppercase tracking-widest mt-1">
                  {FOUNDER.role}
                </p>
              </div>

              {/* Personal quote — the most trust-building element */}
              <blockquote className="relative pl-4 border-l-2 border-[#FF5E00]/60 mb-6">
                <p className="text-lg sm:text-xl text-zinc-100 leading-relaxed italic">
                  &bdquo;{FOUNDER.quote}&ldquo;
                </p>
              </blockquote>

              <p className="text-sm text-zinc-400 leading-relaxed mb-6">
                Firma <strong className="text-white">RESTOB s.r.o.</strong> pôsobí od roku 2009. Máme vlastný tím murárov, tesárov,
                sadrokartonárov a maliarov — <strong className="text-white">bez poddodávok</strong>. To znamená, že za kvalitu ručím osobne
                a viem, kto je na vašej stavbe každý deň. Naša firma je zapísaná v Obchodnom registri SR a máme{" "}
                <a
                  href="https://finstat.sk/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#00E5FF] hover:underline"
                >
                  overiteľné financie
                </a>.
              </p>

              {/* Direct contact — trust boost, remove friction */}
              <div className="flex flex-wrap gap-3">
                <a
                  href={`tel:${FOUNDER.phone.replace(/\s/g, "")}`}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-sm text-white transition-colors"
                  data-testid="founder-phone"
                >
                  <Phone size={14} className="text-[#00E5FF]" />
                  {FOUNDER.phone}
                </a>
                <a
                  href="https://wa.me/421907649964"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-sm text-white transition-colors"
                  data-testid="founder-whatsapp"
                >
                  <MessageCircle size={14} className="text-emerald-400" />
                  WhatsApp
                </a>
                <a
                  href={`mailto:${FOUNDER.email}`}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-sm text-white transition-colors"
                  data-testid="founder-email"
                >
                  <Mail size={14} className="text-[#00E5FF]" />
                  {FOUNDER.email}
                </a>
                <div className="inline-flex items-center gap-2 px-4 py-2.5 bg-white/[0.02] border border-white/5 rounded-xl text-sm text-zinc-400">
                  <MapPin size={14} className="text-zinc-500" />
                  {FOUNDER.location}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
